import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatRipple } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RepoService } from '../services/repo.service';
import { Site } from '../models/site';
import { MigratedSite } from '../models/migrated-site';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-migrate-sites',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatRipple,
    MatProgressSpinner
  ],
  templateUrl: './migrate-sites.component.html',
  styleUrl: './migrate-sites.component.scss'
})
export class MigrateSitesComponent implements OnInit {
  private readonly repoService = inject(RepoService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  searchControl = new FormControl('');
  filterMigratedControl = new FormControl('');
  private readonly filterMigratedSignal = toSignal(this.filterMigratedControl.valueChanges, { initialValue: '' });

  searchResults = signal<Site[]>([]);
  isSearching = signal(false);
  hasSearched = signal(false);
  searchError = signal<string | null>(null);
  selectedSite = signal<Site | null>(null);

  migratedSites = signal<MigratedSite[]>([]);
  isLoadingMigrated = signal(false);
  migratedError = signal<string | null>(null);

  isAddingSiteId = signal<string | null>(null);
  isDeletingSiteId = signal<string | null>(null);

  filteredMigratedSites = computed(() => {
    const filter = (this.filterMigratedSignal() ?? '')?.toLowerCase().trim();
    const all = this.migratedSites();
    if (!filter) return all;
    return all.filter(s =>
      s.name?.toLowerCase().includes(filter) ||
      s.siteId?.toLowerCase().includes(filter)
    );
  });

  ngOnInit(): void {
    this.loadMigratedSites();

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        const trimmed = term?.trim() || '';
        if (trimmed.length >= 2) {
          this.isSearching.set(true);
          this.hasSearched.set(true);
          this.searchError.set(null);
          return this.repoService.getSite(trimmed).pipe(
            catchError(err => {
              console.error('Error searching sites:', err);
              this.searchError.set('Failed to search sites. Please try again.');
              return of([] as Site[]);
            }),
            finalize(() => this.isSearching.set(false))
          );
        }
        this.isSearching.set(false);
        if (!trimmed) {
          this.hasSearched.set(false);
        }
        return of([] as Site[]);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(sites => {
      this.searchResults.set(sites || []);
      // If the selected site is not in the new results, preserve or clear
      if (this.selectedSite() && !sites.some(s => s.id === this.selectedSite()?.id)) {
        // Keep selected if user selected it
      }
    });
  }

  loadMigratedSites(): void {
    this.isLoadingMigrated.set(true);
    this.migratedError.set(null);
    this.repoService.getMigratedSites().pipe(
      catchError(err => {
        console.error('Error fetching migrated sites:', err);
        this.migratedError.set('Failed to load migrated sites');
        return of([] as MigratedSite[]);
      }),
      finalize(() => this.isLoadingMigrated.set(false))
    ).subscribe(sites => {
      this.migratedSites.set(sites || []);
    });
  }

  selectSite(site: Site): void {
    this.selectedSite.set(site);
  }

  isSiteMigrated(siteId: string): boolean {
    return this.migratedSites().some(m => m.siteId === siteId);
  }

  addToMigratedSites(site: Site, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!site) return;

    const siteDisplayName = site.name || site.shortAddress || 'Site ' + site.id;

    if (this.isSiteMigrated(site.id)) {
      this.snackBar.open(`Site "${siteDisplayName}" is already in migrated sites`, 'Close', { duration: 3000 });
      return;
    }

    const payload: MigratedSite = {
      siteId: site.id,
      name: siteDisplayName,
      coordinates: {
        lat: site.coordinates?.lat ?? 0,
        lng: site.coordinates?.lng ?? 0
      }
    };

    this.isAddingSiteId.set(site.id);
    this.repoService.saveMigratedSite(payload).subscribe({
      next: () => {
        this.isAddingSiteId.set(null);
        this.snackBar.open(`Site "${siteDisplayName}" added to migrated sites`, 'Close', { duration: 3000 });
        this.migratedSites.update(current => [payload, ...current.filter(s => s.siteId !== site.id)]);
      },
      error: (err) => {
        this.isAddingSiteId.set(null);
        console.error('Error saving migrated site:', err);
        this.snackBar.open('Failed to save site to migrated sites', 'Close', { duration: 4000 });
      }
    });
  }

  removeFromMigratedSites(site: MigratedSite, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!site) return;

    this.isDeletingSiteId.set(site.siteId);
    this.repoService.deleteMigratedSite(site.siteId).subscribe({
      next: () => {
        this.isDeletingSiteId.set(null);
        this.snackBar.open(`Site "${site.name}" removed from migrated sites`, 'Close', { duration: 3000 });
        this.migratedSites.update(current => current.filter(s => s.siteId !== site.siteId));
      },
      error: (err) => {
        this.isDeletingSiteId.set(null);
        console.error('Error removing site from migrated sites:', err);
        this.snackBar.open('Failed to remove site from migrated sites', 'Close', { duration: 4000 });
      }
    });
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.searchResults.set([]);
    this.hasSearched.set(false);
  }
}
